import { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Container, Paper, Typography, Alert } from '@mui/material';
import { useUserContext } from 'src/components/ProtectedRoute/userProvider';
import { withdraw, getSales, getWithdrawals, getAccountBalance, getUser, getStripeDashboardUrl } from '../../../apiCalls'; // Import getUser to fetch Stripe status
import SalesTable from './SalesTable';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import logger from 'src/logger';
import { Link } from 'react-router-dom';

function Dashboard() {
  const [accountBalance, setAccountBalance] = useState(0);
  const [recentSales, setRecentSales] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const { user } = useUserContext();

  // State to manage Stripe onboarding status
  const [stripeOnboardingComplete, setStripeOnboardingComplete] = useState<boolean | null>(null);
  const [stripeDashboardUrl, setStripeDashboardUrl] = useState('');
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  // State for sorting and pagination for Sales
  const [salesOrder, setSalesOrder] = useState<'asc' | 'desc'>('asc');
  const [salesOrderBy, setSalesOrderBy] = useState('purchaseCost');
  const [salesPage, setSalesPage] = useState(0);
  const [salesRowsPerPage, setSalesRowsPerPage] = useState(5);


  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.accessToken && user?.uid) {
        try {
          const userData = await getUser(user.accessToken, user.uid);
          
          // Handle case where userData might be null
          if (userData) {
            setStripeOnboardingComplete(userData.stripeOnboardingComplete);
          } else {
            // Handle case where user data doesn't exist yet
            setStripeOnboardingComplete(false);
          }
        } catch (error) {
          logger.error('Error fetching user data:', error);
          setStripeOnboardingComplete(false);
        }
      } 
    };
  
    fetchUserData();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First check if user exists
        const userData = await getUser(user.accessToken, user.uid);
        
        if (userData) {
          // User exists, fetch all the data
          const [sales, balance] = await Promise.all([
            getSales(user.accessToken, user.uid),
            getAccountBalance(user.accessToken, user.uid),
            // getWithdrawals(user.accessToken, user.uid),
          ]);

          setRecentSales(sales);
          setAccountBalance(balance);
          setStripeOnboardingComplete(userData.stripeOnboardingComplete);
          logger.info('user data', userData);
          
          // Fetch Stripe dashboard URL if onboarding is complete
          if (userData.stripeOnboardingComplete) {
            const dashboardUrl = await getStripeDashboardUrl(user.accessToken, user.uid);
            setStripeDashboardUrl(dashboardUrl);
          }
        } else {
          // User doesn't exist yet - set defaults
          setRecentSales([]);
          setAccountBalance(0);
          setStripeOnboardingComplete(false);
        }
      } catch (error) {
        logger.error('Error fetching data:', error);
        // Set defaults on error
        setRecentSales([]);
        setAccountBalance(0);
        setStripeOnboardingComplete(false);
      } finally {
        setLoadingDashboard(false);
      }
    };

    fetchData();
  }, [user.uid, user.accessToken]);

  
  const handleOpenStripeDashboard = async () => {
    try {
      const dashboardUrl = await getStripeDashboardUrl(user.accessToken, user.uid);
      window.open(dashboardUrl, '_blank'); // Open in new tab
    } catch (error) {
      logger.error('Error opening Stripe dashboard:', error);
    }
  };
  const handleSalesRequestSort = (property) => {
    const isAsc = salesOrderBy === property && salesOrder === 'asc';
    setSalesOrder(isAsc ? 'desc' : 'asc');
    setSalesOrderBy(property);
  };

  const handleWithdraw = async (amount) => {
    try {
      await withdraw(user.accessToken, user.uid, amount);
      const updatedWithdrawals = await getWithdrawals(user.accessToken, user.uid);
      setWithdrawals(updatedWithdrawals); // Update withdrawals after successful transaction
    } catch (error) {
      logger.error('Error during withdrawal:', error);
    }
  };

  const sortedSales = [...recentSales].sort((a, b) => {
    if (salesOrder === 'asc') {
      return a[salesOrderBy] < b[salesOrderBy] ? -1 : 1;
    }
    return a[salesOrderBy] > b[salesOrderBy] ? -1 : 1;
  });

  const sortedWithdrawals = [...withdrawals].sort((a, b) => {
    return new Date(a.withdrawalDate).getTime() - new Date(b.withdrawalDate).getTime();
  });

  const handleSalesChangePage = (event, newPage) => {
    setSalesPage(newPage);
  };

  const handleSalesChangeRowsPerPage = (event) => {
    setSalesRowsPerPage(parseInt(event.target.value, 10));
    setSalesPage(0);
  };


  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {loadingDashboard ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 'medium',
                mb: 2 
              }}
            >
              Sales Dashboard
            </Typography>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                bgcolor: 'background.alternate',
                borderRadius: 2,
                mb: 4
              }}
            >
              <Typography 
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 800 }}
              >
                Here are the location access purchases for your catch reports. 
                A 30% platform fee is added to each sale. Once verified, payouts 
                are made automatically to your bank account via Stripe.
              </Typography>
            </Paper>
          </Box>

          {/* Stripe Setup Section - Only show if onboarding is not complete */}
          {!stripeOnboardingComplete && (
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3,
                mb: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(236, 236, 255, 0.9)',
                border: '1px solid #c4c4ff'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
                <Box sx={{ mb: { xs: 2, md: 0 } }}>
                  <Typography variant="h6" gutterBottom>
                    Set Up Your Payment Account
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1, maxWidth: 600 }}>
                    To receive payments for your sales, you need to complete your Stripe account setup. 
                    Without a Stripe account, you won't be able to receive payments from your catch reports.
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/stripe/setup"
                  variant="contained"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  startIcon={<AccountBalanceWalletIcon />}
                  sx={{ 
                    minWidth: 220,
                    py: 1.5,
                    fontSize: '0.95rem',
                    fontWeight: 'medium',
                    boxShadow: 2
                  }}
                >
                  Complete Stripe Setup
                </Button>
              </Box>
            </Paper>
          )}
          {/* Stripe Dashboard Button - Only show if onboarding is complete */}
          {stripeOnboardingComplete === true && (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 3,
                mb: 4,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Manage Your Payouts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View your balance, update bank details, and manage payouts in your Stripe dashboard.
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenStripeDashboard}
                startIcon={<AccountBalanceIcon />}
                sx={{ minWidth: 200 }}
              >
                Open Stripe Dashboard
              </Button>
            </Paper>
          )}

          {/* Sales Table */}
          <Box sx={{ mb: 4 }}>
            <SalesTable
              salesOrderBy={salesOrderBy}
              salesOrder={salesOrder}
              handleSalesRequestSort={handleSalesRequestSort}
              sortedSales={sortedSales}
              salesPage={salesPage}
              salesRowsPerPage={salesRowsPerPage}
              handleSalesChangePage={handleSalesChangePage}
              handleSalesChangeRowsPerPage={handleSalesChangeRowsPerPage}
              recentSales={recentSales}
              accountBalance={accountBalance}
            />
          </Box>

          {/* Withdrawals Table */}
          {/* <WithdrawalsTable
            sortedWithdrawals={sortedWithdrawals}
            withdrawalsPage={withdrawalsPage}
            withdrawalsRowsPerPage={withdrawalsRowsPerPage}
            withdrawals={withdrawals}
            handleWithdrawalsChangePage={handleWithdrawalsChangePage}
            handleWithdrawalsChangeRowsPerPage={handleWithdrawalsChangeRowsPerPage}
          /> */}
        </>
      )}
    </Container>
  );
}

export default Dashboard;